import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { encryptPassword, encodeMetadata } from '../utils/crypto';
import './PasswordGenerator.css';

interface PasswordOptions {
  length: number;
  lower: boolean;
  upper: boolean;
  digits: boolean;
  symbols: boolean;
  excludeConfusing: boolean;
  limitSpecialSymbols: boolean;
  noConsecutiveIdentical: boolean;
}

interface PasswordResult {
  password: string;
}

const faqContent = [
  {
    question: 'Is PasswordGenius really free to use?',
    answer: 'Yes. PasswordGenius is a free online password generator. You can create unlimited strong passwords and share them securely without creating an account.'
  },
  {
    question: 'How does the password sharing link stay secure?',
    answer: 'Passwords are encrypted locally in your browser using the Web Crypto API before they ever leave your device. Only the person with the generated link and embedded decryption key can read the secret.'
  },
  {
    question: 'What character sets should I enable for the strongest password?',
    answer: 'Keeping lowercase, uppercase, numbers, and symbols enabled results in high-entropy passwords that are difficult to brute force. You can also exclude confusing characters or limit special symbols when compliance rules require it.'
  },
  {
    question: 'Can I use PasswordGenius for corporate credentials?',
    answer: 'Yes. The generator follows security best practices, offers policy-friendly toggles, and never stores your passwords unless you opt into encrypted share links with automatic expiry.'
  }
];

const PasswordGenerator: React.FC = () => {
  const [options, setOptions] = useState<PasswordOptions>({
    length: 12,
    lower: true,
    upper: true,
    digits: true,
    symbols: true,
    excludeConfusing: true,
    limitSpecialSymbols: true,
    noConsecutiveIdentical: true,
  });

  const [generatedPassword, setGeneratedPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
  const [shareLink, setShareLink] = useState<string>('');
  const [shareError, setShareError] = useState<string>('');
  const [shareLoading, setShareLoading] = useState<boolean>(false);
  const [shareCopied, setShareCopied] = useState<boolean>(false);
  const [shareConfig, setShareConfig] = useState<{ ttlSeconds: number; oneTime: boolean; username: string }>({
    ttlSeconds: 86400,
    oneTime: true,
    username: '',
  });

  const shareTtlOptions = useMemo(() => ([
    { label: '15 minutes', value: 900 },
    { label: '1 hour', value: 3600 },
    { label: '24 hours', value: 86400 },
    { label: '7 days', value: 604800 },
  ]), []);

  useEffect(() => {
    if (!generatedPassword) {
      setShareModalOpen(false);
      setShareLink('');
      setShareError('');
      setShareCopied(false);
    }
  }, [generatedPassword]);

  const generatePassword = useCallback(async () => {
    // Validate that at least one character set is selected
    if (!options.lower && !options.upper && !options.digits && !options.symbols) {
      setError('Please select at least one character set.');
      return;
    }

    setIsLoading(true);
    setError('');
    setGeneratedPassword('');

    try {
      const params = new URLSearchParams({
        length: options.length.toString(),
        lower: options.lower.toString(),
        upper: options.upper.toString(),
        digits: options.digits.toString(),
        symbols: options.symbols.toString(),
        excludeConfusing: options.excludeConfusing.toString(),
        limitSpecialSymbols: options.limitSpecialSymbols.toString(),
        noConsecutiveIdentical: options.noConsecutiveIdentical.toString(),
      });

      const response = await fetch(`/api/password/generate?${params}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate password');
      }

      const data: PasswordResult = await response.json();
      setGeneratedPassword(data.password);
    } catch (err) {
      console.error('Error generating password:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [options]);

  const copyToClipboard = useCallback(async () => {
    if (generatedPassword) {
      try {
        await navigator.clipboard.writeText(generatedPassword);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy password:', err);
        setError('Failed to copy password to clipboard.');
      }
    }
  }, [generatedPassword]);

  const calculateStrength = useCallback((password: string): { level: string; class: string } => {
    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      digits: /[0-9]/.test(password),
      symbols: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };

    // Length scoring
    if (password.length >= 12) score += 2;
    else if (password.length >= 8) score += 1;

    // Character variety scoring
    Object.values(checks).forEach(check => {
      if (check) score += 1;
    });

    // Entropy bonus for very long passwords
    if (password.length >= 20) score += 1;

    if (score <= 3) return { level: 'Weak', class: 'weak' };
    if (score <= 5) return { level: 'Medium', class: 'medium' };
    if (score <= 7) return { level: 'Strong', class: 'strong' };
    return { level: 'Very Strong', class: 'very-strong' };
  }, []);

  const handleOptionChange = useCallback((key: keyof PasswordOptions, value: boolean | number) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  }, []);

  const openShareModal = useCallback(() => {
    if (!generatedPassword) {
      return;
    }
    setShareModalOpen(true);
    setShareError('');
  }, [generatedPassword]);

  const closeShareModal = useCallback(() => {
    setShareModalOpen(false);
    setShareLoading(false);
  }, []);

  const handleCreateShareLink = useCallback(async () => {
    if (!generatedPassword) {
      return;
    }

    if (!window.isSecureContext || !window.crypto?.subtle) {
      setShareError('Secure context required. Please use HTTPS to share passwords.');
      return;
    }

    setShareLoading(true);
    setShareError('');

    try {
      const encrypted = await encryptPassword(generatedPassword);
      const metadata = encodeMetadata({
        length: generatedPassword.length,
        ...(shareConfig.username.trim() && { username: shareConfig.username.trim() }),
      });
      const payload = {
        ciphertext: encrypted.ciphertext,
        iv: encrypted.iv,
        ttlSeconds: shareConfig.ttlSeconds,
        oneTime: shareConfig.oneTime,
        metadata,
      };

      const response = await fetch('/api/share', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let message = 'Failed to create share link. Please try again.';
        try {
          const errorBody = await response.json();
          if (errorBody?.error) {
            message = errorBody.error;
          }
        } catch (parseError) {
          // ignore parse errors
        }
        throw new Error(message);
      }

      const data = await response.json();
      const url = `${window.location.origin}/share/${data.id}#k=${encrypted.key}`;
      setShareLink(url);
      setShareCopied(false);
    } catch (err) {
      console.error('Failed to create password share link:', err);
      setShareError(err instanceof Error ? err.message : 'Failed to create share link. Please try again.');
    } finally {
      setShareLoading(false);
    }
  }, [generatedPassword, shareConfig]);

  const handleCopyShareLink = useCallback(async () => {
    if (!shareLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(shareLink);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy share link:', err);
      setShareError('Failed to copy link to clipboard.');
    }
  }, [shareLink]);

  const strength = generatedPassword ? calculateStrength(generatedPassword) : null;

  // Build a mailto link for sending the generated password
  const emailMailtoLink = useMemo(() => {
    if (!generatedPassword) return '';
    const subject = encodeURIComponent('Your generated password from PasswordGenius');
    const body = encodeURIComponent(
      `Here is your generated password:\n\n${generatedPassword}\n\nGenerated via PasswordGenius.`
    );
    return `mailto:?subject=${subject}&body=${body}`;
  }, [generatedPassword]);

  // Use neutral class names to avoid ad blockers hiding generic "share" classes
  const emailButtonClass = useMemo(() => (
    emailMailtoLink ? 'email-btn email-btn--primary' : 'email-btn email-btn--disabled'
  ), [emailMailtoLink]);

  return (
    <div className="container">
      <header className="header">
        <h1 className="title">🔐 Free secure online password generator – PasswordGenius</h1>
        <p className="subtitle">Generate strong, unique passwords in your browser</p>
        <p className="version">Version 2.4 - Secure Link Sharing 🔗</p>
      </header>

      <main className="main">
        <div className="card">
          <form 
            className="form" 
            onSubmit={(e) => {
              e.preventDefault();
              generatePassword();
            }}
          >
            <div className="form-group">
              <label htmlFor="length" className="label">Password Length</label>
              <div className="input-group">
                <input
                  type="range"
                  id="length"
                  min="8"
                  max="24"
                  value={options.length}
                  onChange={(e) => handleOptionChange('length', parseInt(e.target.value))}
                  className="slider"
                />
                <span className="length-value">{options.length}</span>
              </div>
            </div>

            <div className="form-group">
              <label className="label">Character Sets</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={options.lower}
                    onChange={(e) => handleOptionChange('lower', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-text">Lowercase (a-z)</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={options.upper}
                    onChange={(e) => handleOptionChange('upper', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-text">Uppercase (A-Z)</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={options.digits}
                    onChange={(e) => handleOptionChange('digits', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-text">Numbers (0-9)</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={options.symbols}
                    onChange={(e) => handleOptionChange('symbols', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-text">Symbols (!@#$%^&*)</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="label">Advanced Options</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={options.excludeConfusing}
                    onChange={(e) => handleOptionChange('excludeConfusing', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-text">Exclude confusing characters (I, 0, O, l)</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={options.limitSpecialSymbols}
                    onChange={(e) => handleOptionChange('limitSpecialSymbols', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-text">Limit to one special symbol</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={options.noConsecutiveIdentical}
                    onChange={(e) => handleOptionChange('noConsecutiveIdentical', e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="checkbox-text">Disallow consecutive identical characters</span>
                </label>
              </div>
            </div>

            <button 
              type="submit" 
              className="generate-btn" 
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="btn-loading">Generating...</span>
              ) : (
                <span className="btn-text">Generate Password</span>
              )}
            </button>
          </form>

          {generatedPassword && (
            <div className="result-section">
              <div className="password-row">
                <input
                  type="text"
                  value={generatedPassword}
                  readOnly
                  className="password-input"
                />
                <div className="password-actions">
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className={`password-action copy-btn ${copySuccess ? 'success' : ''}`}
                    title="Copy to clipboard"
                    aria-label="Copy generated password"
                  >
                    <span className="password-action-icon copy-icon" aria-hidden="true">📋</span>
                    <span className="password-action-text copy-text">
                      {copySuccess ? 'Copied' : 'Copy'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="password-action link-share-btn"
                    onClick={openShareModal}
                    title="Create secure share link"
                    aria-label="Create secure share link"
                  >
                    <span className="password-action-icon link-share-icon" aria-hidden="true">🔗</span>
                    <span className="password-action-text link-share-text">Share via link</span>
                  </button>
                  {emailMailtoLink && (
                    <a
                      className={`password-action ${emailButtonClass}`}
                      href={emailMailtoLink}
                      title="Send password via email"
                      aria-label="Send generated password via email"
                    >
                      <span className="password-action-icon email-icon" aria-hidden="true">✉️</span>
                      <span className="password-action-text email-text">Send via email</span>
                    </a>
                  )}
                </div>
              </div>
              <div className="password-info">
                <div className="info-item">
                  <span className="info-label">Length:</span>
                  <span className="info-value">{generatedPassword.length}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Strength:</span>
                  <span className={`info-value strength-${strength?.class}`}>
                    {strength?.level}
                  </span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        {shareModalOpen && (
          <div className="share-modal" role="dialog" aria-modal="true" aria-labelledby="share-modal-title">
            <div className="share-modal__overlay" onClick={closeShareModal} aria-hidden="true"></div>
            <div className="share-modal__content">
              <button
                type="button"
                className="share-modal__close"
                onClick={closeShareModal}
                aria-label="Close share modal"
              >
                ×
              </button>
              <h2 className="share-modal__title" id="share-modal-title">Share password via secure link</h2>
              <p className="share-modal__description">
                Your password is encrypted in your browser before it is stored. Only people with this link can decrypt it.
              </p>

              <div className="share-modal__field">
                <label htmlFor="share-username" className="share-modal__label">Username (optional)</label>
                <input
                  id="share-username"
                  type="text"
                  className="share-modal__input"
                  placeholder="e.g. john@example.com"
                  value={shareConfig.username}
                  onChange={(e) => setShareConfig(prev => ({ ...prev, username: e.target.value }))}
                  maxLength={100}
                />
              </div>

              <div className="share-modal__field">
                <label htmlFor="share-ttl" className="share-modal__label">Link expires in</label>
                <select
                  id="share-ttl"
                  className="share-modal__select"
                  value={shareConfig.ttlSeconds}
                  onChange={(e) => setShareConfig(prev => ({ ...prev, ttlSeconds: parseInt(e.target.value, 10) }))}
                >
                  {shareTtlOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <label className="share-modal__checkbox">
                <input
                  type="checkbox"
                  checked={shareConfig.oneTime}
                  onChange={(e) => setShareConfig(prev => ({ ...prev, oneTime: e.target.checked }))}
                />
                <span>One-time link (expires after first view)</span>
              </label>

              {shareError && (
                <div className="share-modal__error" role="alert">
                  {shareError}
                </div>
              )}

              {shareLink && (
                <div className="share-modal__link-block">
                  <label className="share-modal__label">Share link</label>
                  <div className="share-modal__link-display">
                    <input
                      type="text"
                      value={shareLink}
                      readOnly
                      className="share-modal__input"
                    />
                    <button
                      type="button"
                      className="share-modal__copy"
                      onClick={handleCopyShareLink}
                    >
                      {shareCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <p className="share-modal__hint">
                    Send this link securely. Anyone with it can view the password.
                  </p>
                </div>
              )}

              <div className="share-modal__actions">
                <button
                  type="button"
                  className="share-modal__action-btn"
                  onClick={handleCreateShareLink}
                  disabled={shareLoading}
                >
                  {shareLoading ? 'Creating…' : shareLink ? 'Create new link' : 'Create link'}
                </button>
                <button
                  type="button"
                  className="share-modal__secondary-btn"
                  onClick={closeShareModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="features">
          <h3>Features</h3>
          <ul>
            <li>🔒 Cryptographically secure random generation</li>
            <li>⚙️ Customizable length (8-24 characters)</li>
            <li>🎛️ Flexible character set options</li>
            <li>🚫 Exclude confusing characters (I, 0, O, l)</li>
            <li>🎯 Limit special symbols to one per password</li>
            <li>🚧 Disallow consecutive identical characters</li>
            <li>🔗 Share passwords via secure link or email</li>
            <li>📱 Responsive design for all devices</li>
            <li>⚡ Instant generation and copying</li>
          </ul>
        </div>

        <section className="seo-section" aria-labelledby="seo-overview-title">
          <h2 id="seo-overview-title">Online password generator built for modern security teams</h2>
          <p>
            PasswordGenius combines a cryptographically secure random number generator, flexible policy controls,
            and optional encrypted sharing so you can create unique strong passwords for every account.
            The tool runs completely in your browser which means your secrets never leave your device unless you intentionally
            share them through an expiring link.
          </p>
          <h3>How to generate a strong password</h3>
          <ol className="seo-list">
            <li>Select a length between 12 and 24 characters. Longer passwords exponentially increase entropy.</li>
            <li>Keep lowercase, uppercase, numbers, and symbols enabled for the highest protection.</li>
            <li>Enable compliance toggles such as &quot;limit special symbols&quot; when corporate policies require predictable output.</li>
            <li>Press &quot;Generate Password&quot; and copy it to your password manager or send it via an encrypted link.</li>
          </ol>
          <p>
            Every password is evaluated instantly so you can confirm the strength level.
            Companies use PasswordGenius to onboard employees, rotate privileged credentials, and meet cybersecurity insurance requirements.
          </p>
        </section>

        <section className="faq-section" aria-labelledby="faq-title">
          <h3 id="faq-title">Frequently asked questions</h3>
          <div className="faq-list">
            {faqContent.map((item) => (
              <details key={item.question} className="faq-item">
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default PasswordGenerator;
